import React, { useEffect, useState, useCallback } from "react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
    title: "",
    slug: "",
    category: "",
    description: "",
    thumbnail_url: "",
    download_url: "",
    is_published: true,
    display_order: 0,
};

export default function AlchemistTableAdminPage() {
    const [password, setPassword] = useState(sessionStorage.getItem("alchemy-admin-pw") || "");
    const [authenticated, setAuthenticated] = useState(!!sessionStorage.getItem("alchemy-admin-pw"));
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingSlug, setEditingSlug] = useState(null);
    const [message, setMessage] = useState(null);

    const authHeaders = useCallback(() => ({
        "Content-Type": "application/json",
        "x-admin-password": password,
    }), [password]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/alchemist-table?include_unpublished=true`, {
                headers: { "x-admin-password": password },
            });
            if (res.ok) setItems(await res.json());
        } finally {
            setLoading(false);
        }
    }, [password]);

    useEffect(() => {
        if (authenticated) load();
    }, [authenticated, load]);

    const login = async (e) => {
        e.preventDefault();
        const probe = await fetch(`${API}/api/alchemist-table/verify-admin`, {
            headers: { "x-admin-password": password },
        });
        if (probe.ok) {
            sessionStorage.setItem("alchemy-admin-pw", password);
            setAuthenticated(true);
            setMessage(null);
        } else {
            setMessage({ tone: "error", text: "Wrong password." });
        }
    };

    const logout = () => {
        sessionStorage.removeItem("alchemy-admin-pw");
        setPassword("");
        setAuthenticated(false);
        setItems([]);
        setMessage(null);
    };

    const submit = async (e) => {
        e.preventDefault();
        setMessage(null);
        const url = editingSlug
            ? `${API}/api/alchemist-table/${editingSlug}`
            : `${API}/api/alchemist-table`;
        const method = editingSlug ? "PUT" : "POST";
        const res = await fetch(url, {
            method,
            headers: authHeaders(),
            body: JSON.stringify(form),
        });
        if (res.ok) {
            setMessage({ tone: "ok", text: editingSlug ? "Item updated." : "Item created." });
            setForm(EMPTY_FORM);
            setEditingSlug(null);
            load();
        } else {
            const err = await res.json().catch(() => ({ detail: "Something went wrong." }));
            setMessage({ tone: "error", text: err.detail || "Save failed." });
        }
    };

    const startEdit = (r) => {
        setEditingSlug(r.slug);
        setForm({
            title: r.title || "",
            slug: r.slug || "",
            category: r.category || "",
            description: r.description || "",
            thumbnail_url: r.thumbnail_url || "",
            download_url: r.download_url || "",
            is_published: r.is_published,
            display_order: r.display_order || 0,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const cancelEdit = () => {
        setEditingSlug(null);
        setForm(EMPTY_FORM);
    };

    const del = async (slug) => {
        if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
        const res = await fetch(`${API}/api/alchemist-table/${slug}`, {
            method: "DELETE",
            headers: { "x-admin-password": password },
        });
        if (res.ok) load();
    };

    if (!authenticated) {
        return (
            <main className="bg-ivory min-h-screen">
                <Seo title="Alchemist Table Admin | Alchemy Practice" description="Admin." />
                <Nav />
                <div className="pt-40 pb-20 mx-auto max-w-md px-6">
                    <h1 className="font-serif text-4xl text-charcoal mb-8">Alchemist Table Admin</h1>
                    <form onSubmit={login} className="space-y-4" data-testid="alchemist-admin-login-form">
                        <input
                            type="password"
                            placeholder="Admin password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full p-3 border border-ivory-dark bg-white"
                            data-testid="alchemist-admin-password-input"
                        />
                        <button
                            type="submit"
                            className="w-full py-3 bg-charcoal text-ivory tracking-widest uppercase text-xs"
                            data-testid="alchemist-admin-login-submit"
                        >
                            Sign In
                        </button>
                        {message?.text && (
                            <p className={message.tone === "error" ? "text-red-700" : "text-green-700"}>{message.text}</p>
                        )}
                    </form>
                </div>
                <Footer />
            </main>
        );
    }

    return (
        <main className="bg-ivory min-h-screen">
            <Seo title="Alchemist Table Admin | Alchemy Practice" description="Admin." />
            <Nav />
            <div className="pt-32 pb-16 mx-auto max-w-[1200px] px-6 lg:px-12">
                <div className="flex items-center justify-between mb-8">
                    <h1 className="font-serif text-4xl text-charcoal">
                        Alchemist Table <span className="italic" style={{ color: "#D9A3A0" }}>Admin</span>
                    </h1>
                    <button onClick={logout} className="text-xs tracking-widest uppercase text-charcoal-soft hover:text-charcoal" data-testid="alchemist-admin-logout">
                        Sign Out
                    </button>
                </div>

                {message?.text && (
                    <div
                        data-testid="alchemist-admin-message"
                        className={`mb-6 p-4 border ${message.tone === "error" ? "border-red-300 bg-red-50 text-red-800" : "border-green-300 bg-green-50 text-green-800"}`}
                    >
                        {message.text}
                    </div>
                )}

                <form onSubmit={submit} className="mb-16 p-8 bg-white border border-ivory-dark space-y-4" data-testid="alchemist-form">
                    <h2 className="font-serif text-2xl mb-2">{editingSlug ? `Editing: ${editingSlug}` : "New Item"}</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="text-xs tracking-widest uppercase text-charcoal-soft">Title</span>
                            <input
                                type="text"
                                required
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                className="w-full mt-1 p-2 border border-ivory-dark bg-white"
                                data-testid="alchemist-input-title"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs tracking-widest uppercase text-charcoal-soft">Slug (optional — auto-generated)</span>
                            <input
                                type="text"
                                value={form.slug}
                                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                className="w-full mt-1 p-2 border border-ivory-dark bg-white"
                                data-testid="alchemist-input-slug"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs tracking-widest uppercase text-charcoal-soft">Category</span>
                            <input
                                type="text"
                                list="alchemist-cats"
                                value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                className="w-full mt-1 p-2 border border-ivory-dark bg-white"
                                data-testid="alchemist-input-category"
                            />
                            <datalist id="alchemist-cats">
                                <option value="Tarot" />
                                <option value="Astrology" />
                                <option value="Reflection" />
                                <option value="Ritual" />
                                <option value="Practice" />
                            </datalist>
                        </label>
                        <label className="block">
                            <span className="text-xs tracking-widest uppercase text-charcoal-soft">Display Order (higher = pinned)</span>
                            <input
                                type="number"
                                value={form.display_order}
                                onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value, 10) || 0 })}
                                className="w-full mt-1 p-2 border border-ivory-dark bg-white"
                                data-testid="alchemist-input-order"
                            />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="text-xs tracking-widest uppercase text-charcoal-soft">Thumbnail URL</span>
                            <input
                                type="url"
                                value={form.thumbnail_url}
                                onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
                                className="w-full mt-1 p-2 border border-ivory-dark bg-white"
                                placeholder="https://..."
                                data-testid="alchemist-input-thumb"
                            />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="text-xs tracking-widest uppercase text-charcoal-soft">Download URL (PDF / image)</span>
                            <input
                                type="url"
                                value={form.download_url}
                                onChange={(e) => setForm({ ...form, download_url: e.target.value })}
                                className="w-full mt-1 p-2 border border-ivory-dark bg-white"
                                placeholder="https://..."
                                data-testid="alchemist-input-download"
                            />
                        </label>
                        <label className="block md:col-span-2">
                            <span className="text-xs tracking-widest uppercase text-charcoal-soft">Description</span>
                            <textarea
                                rows={3}
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                className="w-full mt-1 p-2 border border-ivory-dark bg-white"
                                data-testid="alchemist-input-description"
                            />
                        </label>
                        <label className="inline-flex items-center gap-2 md:col-span-2">
                            <input
                                type="checkbox"
                                checked={form.is_published}
                                onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                                data-testid="alchemist-input-published"
                            />
                            <span className="text-sm text-charcoal">Published</span>
                        </label>
                    </div>
                    <div className="flex gap-3 pt-4">
                        <button
                            type="submit"
                            className="px-6 py-3 bg-charcoal text-ivory tracking-widest uppercase text-xs"
                            data-testid="alchemist-submit"
                        >
                            {editingSlug ? "Update Item" : "Create Item"}
                        </button>
                        {editingSlug && (
                            <button
                                type="button"
                                onClick={cancelEdit}
                                className="px-6 py-3 border border-ivory-dark tracking-widest uppercase text-xs"
                                data-testid="alchemist-cancel"
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </form>

                <h2 className="font-serif text-2xl mb-4">All Items {loading ? "…" : `(${items.length})`}</h2>
                <div className="space-y-3" data-testid="alchemist-admin-list">
                    {items.map((r) => (
                        <div
                            key={r.id}
                            className="p-4 bg-white border border-ivory-dark flex items-center justify-between gap-4"
                            data-testid={`alchemist-row-${r.slug}`}
                        >
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                    <span className="font-serif text-lg">{r.title}</span>
                                    {r.category && (
                                        <span className="text-[10px] tracking-widest uppercase" style={{ color: "#D9A3A0" }}>
                                            {r.category}
                                        </span>
                                    )}
                                    {!r.is_published && (
                                        <span className="text-[10px] tracking-widest uppercase text-red-700">Draft</span>
                                    )}
                                </div>
                                <div className="text-xs text-charcoal-soft">
                                    /{r.slug} · order: {r.display_order}
                                </div>
                            </div>
                            <button onClick={() => startEdit(r)} className="text-xs tracking-widest uppercase hover:text-gold" data-testid={`alchemist-edit-${r.slug}`}>
                                Edit
                            </button>
                            <button
                                onClick={() => del(r.slug)}
                                className="text-xs tracking-widest uppercase text-red-700 hover:opacity-70"
                                data-testid={`alchemist-delete-${r.slug}`}
                            >
                                Delete
                            </button>
                        </div>
                    ))}
                </div>
            </div>
            <Footer />
        </main>
    );
}
